import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface ImpersonatedProfile {
  id: string;
  user_id: string;
  username: string;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
  german_artistic_name: string | null;
  is_profile_complete: boolean | null;
  affiliate_code: string | null;
  instagram: string | null;
  phone: string | null;
  address: string | null;
  cpf: string | null;
  cep: string | null;
  numero: string | null;
  estrada: string | null;
  pix_key: string | null;
  wise_id: string | null;
  referred_by_profile_id: string | null;
  internal_answer_1: string | null;
  internal_answer_2: string | null;
  internal_answer_3: string | null;
  internal_answer_4: string | null;
  internal_answer_5: string | null;
  exclusive_content_drive_link: string | null;
  document_front_url: string | null;
  document_back_url: string | null;
}

export function useModelImpersonation() {
  const [searchParams] = useSearchParams();
  const { userRole } = useAuth();
  const viewAsUserId = searchParams.get("viewAs");
  const isImpersonating = userRole === "admin" && !!viewAsUserId;

  const [impersonatedProfile, setImpersonatedProfile] = useState<ImpersonatedProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isImpersonating || !viewAsUserId) {
      setImpersonatedProfile(null);
      return;
    }

    const fetchProfile = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("user_id", viewAsUserId)
          .single();

        if (error) throw error;
        setImpersonatedProfile(data as ImpersonatedProfile);
      } catch (err) {
        console.error("Error fetching impersonated profile:", err);
        setImpersonatedProfile(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [isImpersonating, viewAsUserId]);

  return {
    isImpersonating,
    viewAsUserId,
    impersonatedProfile,
    isLoading,
    /** Returns the effective profile - impersonated if active, otherwise the real one */
    getEffectiveProfileId: (realProfileId: string | undefined) => {
      return isImpersonating && impersonatedProfile ? impersonatedProfile.id : realProfileId;
    },
    getEffectiveUserId: (realUserId: string | undefined) => {
      return isImpersonating && viewAsUserId ? viewAsUserId : realUserId;
    },
  };
}
